import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  User,
  Loader2,
  Ban,
  Clock,
  Sword,
  AlertTriangle,
  Shield,
  Eye,
  Trash2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
  "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png"
};

const getBossImage = (bossName) => {
  const baseName = bossName?.split(" - ")[0];
  return BOSS_IMAGE_MAPPING[baseName] || "https://legends.ml/static/images/lib/monster/6130101.png";
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString();
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const GuestProfileModal = ({ isOpen, onClose, guestAlias }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [guestData, setGuestData] = useState(null);
  const [error, setError] = useState(null);
  const [banning, setBanning] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [banDuration, setBanDuration] = useState(24);
  const [removeKills, setRemoveKills] = useState(true);
  const [showBanConfirm, setShowBanConfirm] = useState(false);
  
  // Check if user is admin
  const isAdmin = user?.is_admin || user?.is_junior_admin;

  const fetchGuestActivity = useCallback(async () => {
    if (!guestAlias || !isAdmin) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/guests/${encodeURIComponent(guestAlias)}/activity`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGuestData(response.data);
    } catch (err) {
      console.error("Failed to fetch guest activity:", err);
      // Don't set error - just set empty guest data so we can still show ban options
      setGuestData({
        guest_id: null,
        guest_alias: guestAlias,
        guest_session: null,
        kills: [],
        kill_attempts: [],
        ban_record: null,
        block_record: null,
        total_kills: 0,
        total_attempts: 0
      });
    } finally {
      setLoading(false);
    }
  }, [guestAlias, token, isAdmin]);

  useEffect(() => {
    if (isOpen && guestAlias && isAdmin) {
      fetchGuestActivity();
      // Reset state when opening
      setShowBanConfirm(false);
      setBanReason("");
      setBanDuration(24);
      setRemoveKills(true);
    }
  }, [isOpen, guestAlias, isAdmin, fetchGuestActivity]);

  const handleBanGuest = async () => {
    if (!guestAlias) return;
    
    setBanning(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/guests/ban`,
        {
          identifier: guestAlias,
          reason: banReason || "Admin ban",
          duration_hours: banDuration,
          remove_kills: removeKills
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Guest banned! ${response.data.removed_kills || 0} kills removed.`);
      setShowBanConfirm(false);
      fetchGuestActivity(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to ban guest");
    } finally {
      setBanning(false);
    }
  };

  const handleUnbanGuest = async () => {
    const identifier = guestData?.guest_alias || guestData?.guest_id || guestAlias;
    if (!identifier) return;
    
    setBanning(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/guests/ban/${encodeURIComponent(identifier)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Guest unbanned");
      fetchGuestActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unban guest");
    } finally {
      setBanning(false);
    }
  };

  if (!isAdmin) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Guest Profile</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4 text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Admin access required to view guest profiles.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-muted-foreground" />
            Guest Profile: {guestAlias}
          </DialogTitle>
          <DialogDescription>
            View guest activity and manage bans
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : guestData && (
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Quick Ban Actions - Always visible at top */}
              {!guestData.ban_record && !showBanConfirm && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-amber-400">
                    <Ban className="w-4 h-4" />
                    Quick Actions
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setBanDuration(0.5);
                        setBanReason("Quick ban - 30 min");
                        setShowBanConfirm(true);
                      }}
                    >
                      Ban 30 min
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setBanDuration(1);
                        setBanReason("Quick ban - 1 hour");
                        setShowBanConfirm(true);
                      }}
                    >
                      Ban 1 hour
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setBanDuration(24);
                        setBanReason("Quick ban - 24 hours");
                        setShowBanConfirm(true);
                      }}
                    >
                      Ban 24 hours
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-destructive text-destructive hover:bg-destructive/10"
                      onClick={() => setShowBanConfirm(true)}
                    >
                      Custom Ban...
                    </Button>
                  </div>
                </div>
              )}

              {/* Guest Info */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Session Info
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Guest Alias:</div>
                  <div className="font-medium text-amber-400">{guestData.guest_alias || guestAlias || "N/A"}</div>
                  
                  <div className="text-muted-foreground">Guest ID:</div>
                  <div className="font-mono text-xs truncate">{guestData.guest_id || "Not tracked"}</div>
                  
                  <div className="text-muted-foreground">Last Seen:</div>
                  <div>{guestData.guest_session ? formatTimeAgo(guestData.guest_session?.last_seen) : "Unknown"}</div>
                  
                  <div className="text-muted-foreground">Total Kills:</div>
                  <div className="font-bold text-primary">{guestData.total_kills}</div>
                  
                  <div className="text-muted-foreground">Kill Attempts:</div>
                  <div>{guestData.total_attempts}</div>
                </div>
              </div>

              {/* Ban Status */}
              {(guestData.ban_record || guestData.block_record) && (
                <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-destructive">
                    <Ban className="w-4 h-4" />
                    Active Restrictions
                  </h3>
                  {guestData.ban_record && (
                    <div className="text-sm space-y-1 mb-2">
                      <p><span className="text-muted-foreground">Admin Ban:</span> {guestData.ban_record.reason || "No reason"}</p>
                      <p><span className="text-muted-foreground">Banned by:</span> {guestData.ban_record.banned_by_username}</p>
                      <p><span className="text-muted-foreground">Until:</span> {formatDate(guestData.ban_record.banned_until)}</p>
                    </div>
                  )}
                  {guestData.block_record && (
                    <div className="text-sm space-y-1">
                      <p><span className="text-muted-foreground">System Block:</span> {guestData.block_record.reason}</p>
                      <p><span className="text-muted-foreground">Until:</span> {formatDate(guestData.block_record.blocked_until)}</p>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={handleUnbanGuest}
                    disabled={banning}
                  >
                    {banning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Lift Ban/Block
                  </Button>
                </div>
              )}

              {/* Recent Kills */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Sword className="w-4 h-4" />
                  Recent Kills ({guestData.kills?.length || 0})
                </h3>
                {guestData.kills?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {guestData.kills.map((kill, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded text-sm">
                        <img 
                          src={getBossImage(kill.boss_name)} 
                          alt={kill.boss_name}
                          className="w-8 h-8 object-contain"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{kill.boss_name}</p>
                          <p className="text-xs text-muted-foreground">
                            CH {(kill.channel || 0) + 1} • {formatTimeAgo(kill.killed_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No kills recorded</p>
                )}
              </div>

              {/* Recent Kill Attempts */}
              {guestData.kill_attempts?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Recent Kill Attempts ({guestData.kill_attempts.length})
                  </h3>
                  <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
                    {guestData.kill_attempts.slice(0, 10).map((attempt, idx) => (
                      <div key={idx} className="flex justify-between p-1 bg-muted/20 rounded">
                        <span>{attempt.boss_name} CH{(attempt.channel_index || 0) + 1}</span>
                        <span className="text-muted-foreground">
                          {new Date(attempt.timestamp * 1000).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ban Controls */}
              {!guestData.ban_record && (
                <div className="border-t pt-4">
                  {!showBanConfirm ? (
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => setShowBanConfirm(true)}
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Ban This Guest
                    </Button>
                  ) : (
                    <div className="space-y-4 p-4 bg-destructive/10 rounded-lg border border-destructive/30">
                      <h4 className="font-semibold text-destructive flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Confirm Ban
                      </h4>
                      
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="ban-reason">Reason (optional)</Label>
                          <Input
                            id="ban-reason"
                            value={banReason}
                            onChange={(e) => setBanReason(e.target.value)}
                            placeholder="Reason for ban"
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="ban-duration">Duration (hours)</Label>
                          <Input
                            id="ban-duration"
                            type="number"
                            value={banDuration}
                            onChange={(e) => setBanDuration(parseInt(e.target.value) || 24)}
                            min={1}
                            max={8760}
                            className="mt-1"
                          />
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <input
                            id="remove-kills"
                            type="checkbox"
                            checked={removeKills}
                            onChange={(e) => setRemoveKills(e.target.checked)}
                            className="rounded"
                          />
                          <Label htmlFor="remove-kills" className="flex items-center gap-1 cursor-pointer">
                            <Trash2 className="w-4 h-4 text-destructive" />
                            Remove all kills from trackers
                          </Label>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => setShowBanConfirm(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          className="flex-1"
                          onClick={handleBanGuest}
                          disabled={banning}
                        >
                          {banning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                          Confirm Ban
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GuestProfileModal;
